import React, { useState, useEffect } from 'react';
import axios from 'axios';
import params from '../config/Params';
import { getItemFromLocalStorage } from '../utils/storageUtils';
import { Helmet } from 'react-helmet';
import ScrollToTop from '../ScrollToTop ';
import { useParams, useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';

const Checkin = () => {
  const { bookingId } = useParams();
  const navigate = useNavigate();

  const [bookingDetails, setBookingDetails] = useState(null);
  const [roomAssignments, setRoomAssignments] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchBookingDetails = async () => {
      const hostData = getItemFromLocalStorage('hostProfile');
      const formData = new FormData();
      formData.append('token', hostData?.host_access_token);
      formData.append('confirmation_code', bookingId);

      setLoading(true);
      try {
        const response = await axios.post(
          `${params.baseURL}${params.endpoints.getBookings}`,
          formData,
          { headers: { 'Content-Type': 'multipart/form-data' } }
        );

        if (response.data.status === 'found') {
          const data = response.data.bookings;
          setBookingDetails(data);
          setRoomAssignments(
            Array.from({ length: data.no_of_rooms_booked }, () => ({
              room_id: '',
              guests: [
                { guest_name: '', guest_identity_proof: null },
                { guest_name: '', guest_identity_proof: null }
              ]
            }))
          );
        } else {
          setError('Booking not found.');
        }
      } catch (err) {
        setError('Error fetching booking details.');
      } finally {
        setLoading(false);
      }
    };

    fetchBookingDetails();
  }, [bookingId]);

  const handleRoomChange = (index, value) => {
    const updated = [...roomAssignments];
    updated[index].room_id = value.replace(/\D/g, '');
    setRoomAssignments(updated);
  };

  const handleGuestChange = (roomIndex, guestIndex, field, value) => {
    const updated = [...roomAssignments];
    updated[roomIndex].guests[guestIndex][field] = value.replace(/[^a-zA-Z\s]/g, '');
    setRoomAssignments(updated);
  };

  const handleFileUpload = (e, roomIndex, guestIndex) => {
    const file = e.target.files[0];
    if (file) {
      const updated = [...roomAssignments];
      updated[roomIndex].guests[guestIndex].guest_identity_proof = file;
      setRoomAssignments(updated);
    }
  };

  const validateInputs = () => {
    for (let i = 0; i < roomAssignments.length; i++) {
      for (let j = 0; j < roomAssignments[i].guests.length; j++) {
        const guest = roomAssignments[i].guests[j];
        if (!guest.guest_name.trim()) {
          setError(`Guest ${j + 1} in Room ${i + 1} name is missing.`);
          return false;
        }
        if (!guest.guest_identity_proof) {
          setError(`Guest ${j + 1} in Room ${i + 1} must upload ID proof.`);
          return false;
        }
      }
    }
    setError(null);
    return true;
  };

  const handleSubmit = async () => {
    if (!validateInputs()) return;

    const formData = new FormData();
    formData.append('stay_booking_id', bookingDetails.stay_booking_id);

    roomAssignments.forEach((room, i) => {
      formData.append(`room_assignments[${i}][room_id]`, room.room_id);
      room.guests.forEach((guest, j) => {
        formData.append(`room_assignments[${i}][guests][${j}][guest_name]`, guest.guest_name);
        if (guest.guest_identity_proof) {
          formData.append(
            `room_assignments[${i}][guests][${j}][guest_identity_proof]`,
            guest.guest_identity_proof
          );
        }
      });
    });

    try {
      setLoading(true);
      const response = await axios.post(
        `${params.baseURL}${params.endpoints.saveCheckin}`,
        formData,
        { headers: { 'Content-Type': 'multipart/form-data' } }
      );

      if (response.data.status === 'success') {
        toast.success('Check-in successful.');
        navigate('/home');
      } else {
        setError('Error saving check-in details.');
      }
    } catch (err) {
      setError('Check-in failed.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div>
      <Helmet>
        <title>Manage Check-in</title>
      </Helmet>
      <ScrollToTop />

      <div className="manage-checkin" style={{width:'800px', margin:'auto'}}>
        <div className="clp-header iwtb mb-0">
          <img src="/assets/img/icons/pointer_icon.svg" alt="Pointer" className="pointer-icon" />
          <h1 className="ws-title m-0">Manage Check-in</h1>
        </div>

        {loading && <p>Loading...</p>}

        {bookingDetails && (
          <div className="booking-details">
            <div className="clp-header">
              <div className="iwtb">
                <p className="bd-title">Booking Details</p>
              </div>
              <div className="row">
                <div className="col-md-6">
                  <div className="iwtb bdbib">
                    <img src="/assets/img/icons/pointer_icon.svg" alt="ID" className="pointer-icon" />
                    <p>Booking ID: {bookingDetails.confirmation_code}</p>
                  </div>
                </div>
                <div className="col-md-6">
                  <div className="iwtb bdbib">
                    <img src="/assets/img/icons/pointer_icon.svg" alt="Guests" className="pointer-icon" />
                    <p>Number of Guests: {bookingDetails.selectedCriterias.adults}</p>
                  </div>
                </div>
              </div>
            </div>

            {roomAssignments.map((room, roomIndex) => (
              <div key={roomIndex} className="roomasbox">
                <div className="clp-header iwtb mb-0">
                  <img src="/assets/img/icons/room_icon_2.png" alt="Room" className="pointer-icon" />
                  <p className="bd-title">Room {roomIndex + 1}</p>
                </div>

                <div className="guest-details-box form-group gdsbx">
                  <label>
                    Room Number:
                    <div className="guest-name-box">
                      <img src="/assets/img/icons/pointer_icon.svg" alt="Edit" className="bd-icon" />
                      <input
                        type="text"
                        className="form-control"
                        value={room.room_id}
                        onChange={(e) => handleRoomChange(roomIndex, e.target.value)}
                      />
                    </div>
                  </label>
                </div>

                {room.guests.map((guest, guestIndex) => (
                  <div key={guestIndex} className="guest-details-box">
                    <div className="form-group gdsbx">
                      <label>
                        Guest {guestIndex + 1} Name:
                        <div className="guest-name-box">
                          <img src="/assets/img/icons/pointer_icon.svg" alt="Edit" className="bd-icon" />
                          <input
                            type="text"
                            className="form-control"
                            value={guest.guest_name}
                            onChange={(e) =>
                              handleGuestChange(roomIndex, guestIndex, 'guest_name', e.target.value)
                            }
                          />
                        </div>
                      </label>
                    </div>
                    <div className="form-group gdsbx">
                      <label>
                        Identity Proof:
                        <div className="guest-name-box">
                          <img src="/assets/img/icons/pointer_icon.svg" alt="Upload" className="bd-icon" />
                          <input
                            type="file"
                            className="form-control"
                            onChange={(e) => handleFileUpload(e, roomIndex, guestIndex)}
                          />
                        </div>
                      </label>
                    </div>
                  </div>
                ))}
              </div>
            ))}

            {error && <p className="error" style={{ color: 'red' }}>{error}</p>}

            <button
              onClick={handleSubmit}
              className="btn btn-black hlbtn"
              disabled={loading}
            >
              Submit
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default Checkin;
